// src/hooks/usePiSocket.jsx
"use client";

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";

const SOCKET_URL = process.env.NEXT_PUBLIC_SOCKET_URL || "http://localhost:4000";
const SOCKET_PATH = "/socket.io";

function getSocket() {
  if (typeof window === "undefined") return null;

  if (!window.__RELOADPI_SOCKET__) {
    window.__RELOADPI_SOCKET__ = io(SOCKET_URL, {
      transports: ["websocket"],
      path: SOCKET_PATH,
      autoConnect: true,
    });
  }
  return window.__RELOADPI_SOCKET__;
}

function readNumber(key, fallback) {
  if (typeof window === "undefined") return fallback;
  const v = Number(window.localStorage.getItem(key));
  return Number.isFinite(v) ? v : fallback;
}

export function usePiSocket() {
  const socketRef = useRef(null);

  const [piRate, setPiRate] = useState(() => {
    const v = readNumber("piRate", NaN);
    return Number.isFinite(v) && v > 0 ? v : null;
  });
  const [basePI, setBasePI] = useState(null);
  const [totalPI, setTotalPI] = useState(null);
  const [quotedUSD, setQuotedUSD] = useState(null); // face USD the server quote (basePI/totalPI) was computed for
  const [feePercent, setFeePercent] = useState(() => readNumber("feePercent", 0));

  // init socket once
  useEffect(() => {
    socketRef.current = getSocket();
  }, []);

  // listeners
  useEffect(() => {
    const s = socketRef.current;
    if (!s) return;

    const onRatePayload = (payloadOrNumber) => {
      const r =
        typeof payloadOrNumber === "number"
          ? payloadOrNumber
          : Number(payloadOrNumber?.piRate);

      if (Number.isFinite(r) && r > 0) {
        setPiRate(r);
        window.localStorage.setItem("piRate", String(r));
      }

      const fp = Number(payloadOrNumber?.feePercent);
      if (Number.isFinite(fp)) {
        setFeePercent(fp);
        window.localStorage.setItem("feePercent", String(fp));
      }

      // Server quote (face * 1.15 at the live rate). The client never computes Pi totals.
      if (typeof payloadOrNumber === "object" && payloadOrNumber?.totalPI != null) {
        setBasePI(payloadOrNumber.basePI ?? null);
        setTotalPI(payloadOrNumber.totalPI);
        setQuotedUSD(Number(payloadOrNumber.productPriceUSD) || null);
      }
    };

    const onCalc = (payload) => {
      setBasePI(payload?.basePI ?? null);
      setTotalPI(payload?.totalPI ?? null);

      const r = Number(payload?.piRate);
      if (Number.isFinite(r) && r > 0) {
        setPiRate(r);
        window.localStorage.setItem("piRate", String(r));
      }

      const fp = Number(payload?.feePercent);
      if (Number.isFinite(fp)) {
        setFeePercent(fp);
        window.localStorage.setItem("feePercent", String(fp));
      }
    };

    // OPTIONAL: debug what the server is actually emitting
    // s.onAny((event, ...args) => console.log("[socket event]", event, args?.[0]));

    // avoid duplicate handlers on HMR
    s.off("piRate", onRatePayload);
    s.off("piRateUpdate", onRatePayload);
    s.off("priceCalculated", onCalc);

    s.on("piRate", onRatePayload);
    s.on("piRateUpdate", onRatePayload);
    s.on("priceCalculated", onCalc);

    return () => {
      s.off("piRate", onRatePayload);
      s.off("piRateUpdate", onRatePayload);
      s.off("priceCalculated", onCalc);
      // s.offAny(); // only if you enabled onAny debug
    };
  }, []);

  const requestPriceUpdate = useCallback((usd, offerId) => {
    const s = socketRef.current;
    const u = Number(usd);
    if (!s) return;
    if (!offerId) return;
    if (!Number.isFinite(u) || u <= 0) return;

    s.emit("calculate", { usd: u, offerId });
  }, []);

  return useMemo(
    () => ({
      piRate,
      basePI,
      totalPI,
      quotedUSD,
      feePercent,
      socket: socketRef.current,
      requestPriceUpdate,
    }),
    [piRate, basePI, totalPI, quotedUSD, feePercent, requestPriceUpdate]
  );
}
